import { startCashfreePayment, supportedWebGateways } from './cashfreePayment';
import { assessmentNavigation } from './assessmentNavigation';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import HomeDashboardLayout from '../HomeDashboardLayout';
import HeaderProfileSection from '../HeaderProfileSection';
import PaymentDetail from './PaymentDetail';
import PaymentMethodd from './PaymentMethodd';
import SubscriptionShimmer from './SubscriptionShimmer';
import { applyCoinCoupon, applyExistingCoins, createRazorpayOrder, fetchAvailableCoins, fetchCoinOrderAmounts, fetchCoinPaymentGateways, removeCoinCoupon, removeExistingCoins } from '../../services/coinPurchaseService';
import { isEnabled } from './subjectCart';
import { SUBSCRIPTION_ROUTES } from './subscriptionRoutes';
import { fetchExamGateways } from '../../services/examSubscriptionService';
import { getStoredUser } from '../../utils/authStorage';
import { readExamCheckout, readExamCoupon, saveExamCheckout, saveExamCoupon } from './examDraft';
import { loadExamRazorpay, rupeesToPaise } from './examPayment';
import '../../dashboard/Dashboard.css';
import '../HomeDashboard.css';
import '../gamification/Coinstore.css';
import '../questionBank/QuestionBankList.css';
import './SelectPaymentMethod.css';
import './SubjectSubscriptions.css';


function loadRazorpay() {
  if (window.Razorpay) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = resolve;
    script.onerror = () => reject(new Error('Unable to load Razorpay checkout.'));
    document.body.appendChild(script);
  });
}

export default function AssessmentSubscriptionPayment({ exam = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const savedCheckout = exam ? readExamCheckout() : null;
  const checkoutState = exam ? (location.state?.examPurchase ? location.state : savedCheckout) : location.state;
  const profile = checkoutState?.profile || (exam ? getStoredUser() : null) || {};
  const purchase = exam ? checkoutState?.examPurchase : location.state?.assessmentPurchase;
  const stateId = purchase?.stateId || 0;
  const candidateOrder = exam ? checkoutState?.examSubscriptionOrder : location.state?.assessmentSubscriptionOrder;
  const requestedOrderId = new URLSearchParams(location.search || '').get('vk_order_id');
  const order = candidateOrder?.vk_order_id && (!requestedOrderId || requestedOrderId === candidateOrder.vk_order_id) ? candidateOrder : null;
  const purchaseType = exam ? 'examSubscription' : 'assessmentSubscription';
  const storePath = exam ? SUBSCRIPTION_ROUTES.examPlans : SUBSCRIPTION_ROUTES.assessmentPlans;
  const paymentPlanId = Number(order?.plan_id || purchase?.paymentPlanId);
  const filterGateways = (items) => supportedWebGateways(items);
  const [amounts, setAmounts] = useState(null);
  const [gateways, setGateways] = useState(() => filterGateways(checkoutState?.paymentGateways || []));
  const [selectedGateway, setSelectedGateway] = useState(() => gateways[0] || null);
  const [availableCoins, setAvailableCoins] = useState(null);
  const [coupon, setCoupon] = useState('');
  const [coins, setCoins] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(Boolean(order));
  const [error, setError] = useState('');
  const actionRef = useRef(false);
  const couponForGatewayRef = useRef('');
  const [amountsConfirmed, setAmountsConfirmed] = useState(false);

  const refresh = useCallback(async (orderId, couponCode = '') => {
    const latestAmounts = await fetchCoinOrderAmounts(orderId);
    const effectiveCoupon = exam ? String(latestAmounts.discounts?.coupon?.code || '') : couponCode;
    const latestGateways = await (exam ? fetchExamGateways(paymentPlanId, effectiveCoupon) : fetchCoinPaymentGateways(couponCode));
    if (latestAmounts.final_amount == null || !Number.isFinite(Number(latestAmounts.final_amount)) || Number(latestAmounts.final_amount) < 0 || (!exam && Number(latestAmounts.final_amount) === 0)) throw new Error('The server did not return a payable amount.');
    setAmounts(latestAmounts);
    if (exam) {
      couponForGatewayRef.current = effectiveCoupon;
      if (effectiveCoupon) setCoupon(effectiveCoupon);
      if (Number(latestAmounts.discounts?.coins?.coins_used) > 0) setCoins(String(latestAmounts.discounts.coins.coins_used));
    }
    const supported = supportedWebGateways(latestGateways);
    setGateways(supported);
    setSelectedGateway((current) => supported.find((item) => Number(item.payment_gateway_id) === Number(current?.payment_gateway_id) && item.slug === current?.slug) || supported[0] || null);
    setAmountsConfirmed(true);
    return { amounts: latestAmounts, gateways: supported };
  }, [exam, paymentPlanId]);

  useEffect(() => {
    if (!order?.vk_order_id) return undefined;
    let active = true;
    refresh(order.vk_order_id).then(async (initial) => {
      fetchAvailableCoins().then((data) => { if (active) setAvailableCoins(data); }).catch(() => {});
      const rememberedCoupon = exam ? readExamCoupon() : '';
      if (active && rememberedCoupon && !initial.amounts.discounts?.coupon?.code) {
        actionRef.current = true; setBusy(true); setAmountsConfirmed(false); setCoupon(rememberedCoupon);
        try {
          const result = await applyCoinCoupon(order.vk_order_id, rememberedCoupon);
          if (!active) return;
          const refreshed = await refresh(order.vk_order_id, String(result.coupon_code || rememberedCoupon));
          saveExamCoupon(refreshed.amounts.discounts?.coupon?.code || '');
          setSuggestions(result.coupons || []);
        } catch (failure) {
          if (!active) return;
          saveExamCoupon('');
          setSuggestions(failure.backend?.dataset?.data?.coupons || []);
          setError(failure.message || 'Unable to apply the saved coupon.');
          // Failed saved coupons must not leave stale totals or block a new method.
          await refresh(order.vk_order_id);
        } finally { actionRef.current = false; if (active) setBusy(false); }
      }
      if (active) setLoading(false);
    }).catch((failure) => {
      if (active) { setError(failure.message || 'Unable to load checkout.'); setLoading(false); }
    });
    return () => { active = false; };
  }, [order?.vk_order_id, refresh, exam]);

  const changeOrder = async (request, couponCode) => {
    if (!order?.vk_order_id || actionRef.current) return;
    actionRef.current = true; setBusy(true); setError('');
    try {
      setAmountsConfirmed(false);
      const result = await request();
      const nextCoupon = couponCode === undefined ? (amounts?.discounts?.coupon?.code || '') : String(result.coupon_code ?? couponCode ?? '');
      couponForGatewayRef.current = nextCoupon;
      const next = await refresh(order.vk_order_id, nextCoupon);
      setCoupon(next.amounts.discounts?.coupon?.code || '');
      setCoins(next.amounts.discounts?.coins?.coins_used ? String(next.amounts.discounts.coins.coins_used) : '');
      setSuggestions(result.coupons || []);
      if (exam && couponCode !== undefined) saveExamCoupon(next.amounts.discounts?.coupon?.code || '');
      fetchAvailableCoins().then(setAvailableCoins).catch(() => {});
    } catch (failure) {
      setSuggestions(failure.backend?.dataset?.data?.coupons || []);
      setError(failure.message || 'Unable to update order.');
    } finally { actionRef.current = false; setBusy(false); }
  };
  const couponApplied = Boolean(amounts?.discounts?.coupon?.code);
  const coinsApplied = Boolean(Number(amounts?.discounts?.coins?.coins_used));
  const handleCoupon = () => {
    if (!couponApplied && !coupon.trim()) return;
    changeOrder(() => couponApplied ? removeCoinCoupon(order.vk_order_id) : applyCoinCoupon(order.vk_order_id, coupon.trim()), couponApplied ? '' : coupon.trim());
  };
  const handleCoins = () => {
    if (!coinsApplied && !/^[1-9]\d*$/.test(coins.trim())) { setError('Enter a positive whole number of coins.'); return; }
    changeOrder(() => coinsApplied ? removeExistingCoins(order.vk_order_id) : applyExistingCoins(order.vk_order_id, coins.trim()));
  };
  const retry = async () => {
    if (!order?.vk_order_id) return;
    setError(''); setLoading(true);
    try { await refresh(order.vk_order_id, couponForGatewayRef.current); }
    catch (failure) { setError(failure.message || 'Unable to refresh checkout.'); }
    finally { setLoading(false); }
  };
  const pay = async () => {
    if (!order?.vk_order_id || !selectedGateway || actionRef.current || (exam && !amountsConfirmed)) return;
    actionRef.current = true; setBusy(true); setError('');
    try {
      const latest = await refresh(order.vk_order_id, couponForGatewayRef.current);
      const gateway = latest.gateways.find((item) => Number(item.payment_gateway_id) === Number(selectedGateway.payment_gateway_id) && item.slug === selectedGateway.slug);
      if (!gateway) throw new Error('Please select an available payment method.');
      const payable = Number(latest.amounts.final_amount);
      if (payable <= 0) throw new Error('This order needs server confirmation before it can be completed.');
      const pendingState = { ...(!exam ? assessmentNavigation(purchase) : {}), vkOrderId: order.vk_order_id, purchaseType, stateId, storePath };
      const resultPath = exam ? `${SUBSCRIPTION_ROUTES.paymentResult}?kind=exam` : SUBSCRIPTION_ROUTES.paymentResult;
      if (['bank-transfer', 'upi-transfer'].includes(gateway.slug)) {
        sessionStorage.setItem('subscriptionPurchasePending', JSON.stringify(pendingState));
        if (exam) saveExamCheckout({ examSubscriptionOrder: order, examPurchase: purchase, paymentGateway: gateway });
        navigate(exam ? `${SUBSCRIPTION_ROUTES.manualTransfer}?kind=exam` : SUBSCRIPTION_ROUTES.manualTransfer, { state: exam ? { examSubscriptionOrder: order, examPurchase: purchase, paymentGateway: gateway } : { assessmentSubscriptionOrder: order, assessmentPurchase: purchase, paymentGateway: gateway } });
        return;
      }
      if (gateway.slug === 'cashfree') {
        await startCashfreePayment({ orderId: order.vk_order_id, gateway, pendingState,
          navigate, resultPath, amount: payable });
        return;
      }
      if (gateway.slug !== 'razorpay' || !gateway.client_id) throw new Error('This payment method is unavailable on web.');
      await (exam ? loadExamRazorpay() : loadRazorpay());
      const providerOrder = await createRazorpayOrder(order.vk_order_id, gateway.payment_gateway_id);
      const expectedMinorAmount = exam ? rupeesToPaise(latest.amounts.final_amount) : Math.round(payable * 100);
      const providerAmount = Number(providerOrder.amount);
      if (Number.isFinite(providerAmount) && providerAmount > 0 && providerAmount !== expectedMinorAmount && providerAmount !== payable) {
        throw new Error('Payment amount does not match the confirmed order amount.');
      }
      sessionStorage.setItem('subscriptionPurchasePending', JSON.stringify(pendingState));
      const checkout = new window.Razorpay({
        key: gateway.client_id,
        amount: expectedMinorAmount,
        currency: providerOrder.currency || 'INR',
        order_id: providerOrder.razorpay_order_id,
        name: 'Vidyakasam', description: purchase?.name || (exam ? 'Exam Subscription' : 'Assessment Subscription'),
        prefill: { contact: profile.phone || profile.mobile || '', email: profile.email || '' },
        handler: (response) => navigate(resultPath, { state: { ...pendingState, paymentId: response.razorpay_payment_id } }),
        modal: { ondismiss: () => { actionRef.current = false; setBusy(false); if (exam) navigate(resultPath, { state: pendingState }); } },
      });
      checkout.on('payment.failed', (failure) => { actionRef.current = false; setBusy(false); if (exam) navigate(resultPath, { state: pendingState }); else setError(failure?.error?.description || 'Payment failed.'); });
      checkout.open();
    } catch (failure) { actionRef.current = false; setBusy(false); setError(failure.message || 'Unable to start payment.'); }
  };

  return <><style>{`.homeDashboard-wrapper .page-content { padding-right: 0; } .homeDashboard-wrapper .main-content { height: 100vh; overflow: hidden; box-sizing: border-box; padding-bottom: 35px; min-height: 0; }`}</style><HomeDashboardLayout showHeader={false}><div className="qbl-page qbankList-page page-padding start-gamification coin-payment-page"><div className="start-quiz qbl-main instruction-main">
    <div className="hideProfile-page"><div className="categories-page-header mb-0"><button type="button" className="categories-back-button" onClick={() => navigate(exam ? SUBSCRIPTION_ROUTES.examDetail : purchase?.kind === 'assessment_plan' ? SUBSCRIPTION_ROUTES.assessmentDetail : SUBSCRIPTION_ROUTES.assessmentPlans, { state: { ...(!exam ? assessmentNavigation(purchase) : {}), stateId, planId: purchase?.planId, durationId: purchase?.durationId, generatedOrder: order } })} aria-label={exam ? "Back to exam plans" : "Back to assessment plans"}><i className="bi bi-chevron-left" /></button><h4 className="categories-page-title">Payment Method</h4><HeaderProfileSection /></div></div>
    <div className="coine-balance-wrapper"><div className="rapid-fire-header"><h1 className="qb-title">Payment <span>Method</span></h1></div>
      {!order || !purchase ? <p role="alert">{exam ? "Select an exam plan before checkout." : "Select an assessment plan before checkout."} <button type="button" onClick={() => navigate(storePath, { state: !exam ? { ...assessmentNavigation(purchase), stateId } : undefined })}>Choose a plan</button></p> : <div className="detail-main-box coin-payment-columns"><section className="qbank-LeftContent">
        <PaymentDetail purchaseType={purchaseType} pack={purchase} orderAmounts={exam ? amounts : undefined} order={{ ...order, coupon_discount: amounts?.discounts?.coupon?.discount_amount || 0 }} payable={amounts?.final_amount} amountLoading={loading && !amounts} couponCode={coupon} setCouponCode={(code) => { setCoupon(code); setError(''); }} appliedCoupon={amounts?.discounts?.coupon?.code || ''} coins={coins} setCoins={setCoins} appliedCoins={coinsApplied ? coins : ''} availableCoins={availableCoins?.available_coins} conversionLabel={availableCoins?.coins_per_rupee ? `1 rupee = ${availableCoins.coins_per_rupee} coins` : ''} showCoins={isEnabled(availableCoins?.conversion_enabled) || coinsApplied} busy={busy || loading} onCoupon={handleCoupon} onCoins={handleCoins} error={error} suggestions={suggestions} onSuggestion={(code) => { setCoupon(code); changeOrder(() => applyCoinCoupon(order.vk_order_id, code), code); }} onRetry={error ? retry : undefined} />
        {exam && amounts?.final_amount != null && Number(amounts.final_amount) === 0 && <p>This order has no payable balance. <button type="button" onClick={() => navigate(`${SUBSCRIPTION_ROUTES.paymentResult}?kind=exam`, { state: { vkOrderId: order.vk_order_id, purchaseType, stateId } })}>Check payment status</button> to confirm completion.</p>}
        {exam && amountsConfirmed && !loading && !error && !gateways.length && Number(amounts?.final_amount) > 0 && <p role="status">No payment methods are currently available. <button type="button" disabled={busy} onClick={retry}>Retry payment methods</button></p>}
      </section><aside>{loading && !amounts ? <SubscriptionShimmer kind="paymentMethods" /> : <PaymentMethodd gateways={gateways} selectedGateway={selectedGateway} onSelect={setSelectedGateway} onPay={pay} busy={busy || loading} payable={amounts?.final_amount} ready={Boolean(order?.vk_order_id && amounts && (!exam || amountsConfirmed))} />}</aside></div>}
    </div>
  </div></div></HomeDashboardLayout></>;
}
